import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Loader2, Check } from "lucide-react";
import { api, timeAgo } from "@/lib/api";
import { toast } from "sonner";
import PlaidConnect from "@/components/PlaidConnect";
import { LOGOS } from "@/lib/logos";

const CATEGORIES = ["Productivity", "Communication", "Accounting", "HR & Payroll", "Cards"];

const logoInitials = (name) => {
  const words = name.split(" ");
  return words.length > 1 ? (words[0][0] + words[1][0]).toUpperCase() : name.slice(0, 2).toUpperCase();
};

export default function Integrations() {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/integrations").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggle = async (integ) => {
    setBusy(integ.id);
    try {
      const r = await api.post(`/integrations/${integ.id}/toggle`);
      setItems((its) => its.map((i) => (i.id === integ.id ? { ...i, connected: r.data.connected, last_sync: r.data.last_sync } : i)));
      toast.success(r.data.connected ? `${integ.name} connected — initial sync started` : `${integ.name} disconnected`);
    } catch {
      toast.error("Could not update integration");
    } finally {
      setBusy(null);
    }
  };

  const connectedCount = items ? items.filter((i) => i.connected).length : 0;

  return (
    <div className="space-y-6" data-testid="integrations-page">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h2 className="text-[15px] font-semibold tracking-tight">Integration marketplace</h2>
          <p className="text-[11.5px] text-[#A3A3A3]">Aria plugs into your stack — cards, ERP, HR and chat.</p>
        </div>
        {items && <span className="text-[12px] text-[#6b6b6b]"><span className="font-semibold text-[#4ADE80]">{connectedCount}</span> of {items.length} connected</span>}
      </div>

      <PlaidConnect />

      {!items ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">{[...Array(9)].map((_, i) => <Skeleton key={i} className="h-36 rounded-[14px] bg-[#141414]" />)}</div>
      ) : (
        CATEGORIES.map((cat) => {
          const group = items.filter((i) => i.category === cat);
          if (!group.length) return null;
          return (
            <div key={cat}>
              <h3 className="mb-2.5 text-[11px] font-semibold uppercase tracking-wider text-[#6b6b6b]">{cat}</h3>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {group.map((integ) => (
                  <motion.div key={integ.id} whileHover={{ y: -2 }} transition={{ type: "spring", stiffness: 420, damping: 28 }}
                    data-testid={`integration-card-${integ.id}`}
                    className="card-lift rounded-[14px] border border-[#222] bg-[#111111] p-5 card-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        {LOGOS[integ.id] ? (
                          <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-[#242424] bg-[#1a1a1a] p-2"
                            data-testid={`integration-logo-${integ.id}`}>
                            <img src={LOGOS[integ.id]} alt={`${integ.name} logo`} className="h-full w-full object-contain" />
                          </div>
                        ) : (
                          <div className="flex h-10 w-10 items-center justify-center rounded-xl text-[13px] font-bold"
                            style={{ background: `${integ.color}1c`, color: integ.color, border: `1px solid ${integ.color}33` }}>
                            {logoInitials(integ.name)}
                          </div>
                        )}
                        <div>
                          <div className="text-[13.5px] font-medium">{integ.name}</div>
                          {integ.connected ? (
                            <div className="mt-0.5 flex items-center gap-1.5 text-[10.5px] text-[#4ADE80]">
                              <span className="h-1.5 w-1.5 rounded-full bg-[#22C55E]" /> Connected · synced {timeAgo(integ.last_sync)}
                            </div>
                          ) : (
                            <div className="mt-0.5 flex items-center gap-1.5 text-[10.5px] text-[#6b6b6b]">
                              <span className="h-1.5 w-1.5 rounded-full bg-[#444]" /> Not connected
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <p className="mt-3 text-[11.5px] leading-relaxed text-[#A3A3A3]">{integ.description}</p>
                    <Button size="sm" onClick={() => toggle(integ)} disabled={busy === integ.id}
                      data-testid={`integration-toggle-${integ.id}`}
                      className={`mt-3 h-7 px-3 text-[12px] font-medium ${integ.connected
                        ? "border border-[#2a2a2a] bg-transparent text-[#A3A3A3] hover:bg-[#1c1c1c] hover:text-white"
                        : "bg-[#4F8EF7] text-white hover:bg-[#6BA0F8]"}`}>
                      {busy === integ.id ? <Loader2 size={12} className="mr-1 animate-spin" /> : integ.connected ? <Check size={12} className="mr-1" /> : null}
                      {integ.connected ? "Disconnect" : "Connect"}
                    </Button>
                  </motion.div>
                ))}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
